import { usePredictiveDashboard } from "@/hooks/queries/dashboard/usePredictiveDashboard";
import { Badge, Button, Skeleton } from "@/components/ui";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const currencyBRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 0,
});

export function DashboardAI() {
  const { noShows, noShowsLoading, forecast, forecastLoading, refetchForecast, refetchNoShows } =
    usePredictiveDashboard();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* No-show risk */}
      <section className="space-y-3">
        <header className="flex items-start gap-2.5">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
            <AlertTriangle className="size-3.5" strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-app-fg-strong">
              Risco de faltas (No-Show)
            </h2>
            <p className="mt-1.5 text-sm text-app-fg-muted">
              Pacientes com maior probabilidade de cancelamento nos próximos 7 dias.
            </p>
          </div>
        </header>

        <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-premium-sm">
          {noShowsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          ) : !noShows ? (
            <div className="space-y-3"><p role="status">Previsões de faltas indisponíveis.</p><Button variant="outline" onClick={() => void refetchNoShows()}>Atualizar previsões</Button></div>
          ) : (
            <div className="space-y-3">
              {noShows?.predictions.slice(0, 3).map((pred, i) => (
                <article
                  key={i}
                  className="flex flex-col gap-2 rounded-lg border border-app-border-subtle bg-app-surface-muted p-3 transition-colors hover:bg-app-surface"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-app-fg-strong">
                        {pred.patient_name}
                      </p>
                      <p className="truncate text-xs text-app-fg-muted">
                        {pred.date} · {pred.procedure}
                      </p>
                    </div>
                    <Badge
                      variant={
                        pred.risk_score > 0.6 ? "destructive" : "secondary"
                      }
                      className="shrink-0"
                    >
                      {(pred.risk_score * 100).toFixed(0)}% Risco
                    </Badge>
                  </div>

                  <p className="flex items-start gap-1.5 text-xs text-app-fg-muted">
                    <AlertCircle
                      className="mt-0.5 size-3 shrink-0"
                      strokeWidth={2}
                    />
                    <span>Fatores: {pred.risk_factors.join(", ")}</span>
                  </p>

                  <p className="flex items-start gap-1.5 rounded-md bg-brand-soft px-2 py-1.5 text-xs font-medium text-brand">
                    <Sparkles
                      className="mt-0.5 size-3 shrink-0"
                      strokeWidth={2.25}
                    />
                    <span>Ação sugerida: {pred.recommended_action}</span>
                  </p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Financial projection */}
      <section className="space-y-3">
        <header className="flex items-start gap-2.5">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
            <TrendingUp className="size-3.5" strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-app-fg-strong">
              Projeção financeira
            </h2>
            <p className="mt-1.5 text-sm text-app-fg-muted">
              Projeção de receita baseada no histórico de atendimentos e curva de demanda.
            </p>
          </div>
        </header>

        <div className="rounded-xl border border-app-border bg-app-surface p-5 shadow-premium-sm">
          {forecastLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : !forecast ? (
            <div className="space-y-3"><p role="status">Projeção financeira indisponível.</p><Button variant="outline" onClick={() => void refetchForecast()}>Atualizar projeção</Button></div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                {/* 30 days — most prominent */}
                <div className="rounded-lg border border-app-border bg-app-surface p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-app-fg-muted">
                    30 dias
                  </p>
                  <p
                    className="mt-2 text-xl font-semibold leading-tight tracking-tight text-app-fg-strong"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {forecast.forecasts["30_days"]?.projected_revenue == null ? "Indisponível" : currencyBRL.format(forecast.forecasts["30_days"].projected_revenue)}
                  </p>
                  <p className="mt-1 text-[10px] text-app-fg-subtle">
                    Acerto:{" "}
                    {forecast?.forecasts["30_days"]?.confidence_interval == null
                      ? "—"
                      : `${(
                          forecast.forecasts["30_days"].confidence_interval * 100
                        ).toFixed(0)}%`}
                  </p>
                </div>

                {/* 60 days — dimmer */}
                <div className="rounded-lg border border-app-border-subtle bg-app-surface-muted p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-app-fg-subtle">
                    60 dias
                  </p>
                  <p
                    className="mt-2 text-xl font-semibold leading-tight tracking-tight text-app-fg"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {forecast.forecasts["60_days"]?.projected_revenue == null ? "Indisponível" : currencyBRL.format(forecast.forecasts["60_days"].projected_revenue)}
                  </p>
                </div>

                {/* 90 days — dimmest */}
                <div className="rounded-lg border border-app-border-subtle bg-app-surface-muted p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-app-fg-subtle">
                    90 dias
                  </p>
                  <p
                    className="mt-2 text-xl font-semibold leading-tight tracking-tight text-app-fg-muted"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {forecast.forecasts["90_days"]?.projected_revenue == null ? "Indisponível" : currencyBRL.format(forecast.forecasts["90_days"].projected_revenue)}
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 border-t border-app-border-subtle pt-4">
                <p className="text-sm font-semibold text-app-fg-strong">
                  Leituras do histórico
                </p>
                {forecast?.insights.map((insight, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-sm text-app-fg"
                  >
                    <CheckCircle2
                      className="mt-0.5 size-4 shrink-0 text-app-success"
                      strokeWidth={2}
                    />
                    <span>{insight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
